// One GATE ROW in the "Needs you" (Today) / Approvals list, in the prototype's own two shapes: the Today
// `.gate` row (marker + title + one-line sub + a small CTA) and the Approvals `.appr` row (marker + kind
// eyebrow + title + why + auth meta + the action group). The exercise control ROUTES the intent to the
// owning module's Gate (P-GATE: the dashboard sets no approval field); it is disabled while the route is in
// flight so a double-click cannot double-route. A gate NOT bound to the human renders a quiet pill instead
// of a control (bound-owner-only Task start, N-APPROVE-1 AC-4) — the OWNING module is the boundary (G-180).
//
// The APPROVALS variant also carries the DECLINE leg (BUILD-DEC-116 — the charter's "No"): a declinable
// bound gate offers "Decline", which expands a MANDATORY-reason form (G-055 — a decline is recorded,
// never silent; for a task-start it drops the task with the reason as the outcome note). A bound gate
// whose kind has NO owning-module decline renders an honest line instead of a dead control.
//
// A task-start gate's title OPENS the Task-detail modal (its objectId IS the Task id — prototype
// data-open-task), so the human reads the thread before deciding. Tokens only (G-181).

import { useState } from 'react';
import type { ApprovalItem } from '../projections.ts';
import type { RouteResult } from '../api-client.ts';
import { classifyApproval, isApprovalReadyForViewer } from '../approval-triage.ts';
import { Btn, StatusPill } from '../primitives.tsx';
import { useShell } from '../../app/ShellContext.tsx';

/** The visual shape a gate row renders in — the Today gate list or the Approvals inbox. */
export type GateRowVariant = 'gate' | 'appr';

/** The marker/eyebrow families the prototype gives each Gate kind (screens.js approvals `fam`). */
const KIND_FAMILY: Readonly<Record<ApprovalItem['kind'], 'own' | 'state'>> = {
  task_draft: 'own',
  task_start: 'own',
  metric_activation: 'state',
  schedule_activation: 'state',
  metric_value_authoritative: 'state',
  hubspot_write: 'state',
};

/** A pending-value gate carries the attention marker (the prototype's `pending` tone). */
function isPendingTone(kind: ApprovalItem['kind']): boolean {
  return kind === 'metric_value_authoritative';
}

/** What declining THIS gate kind durably does — stated up front, never a surprise (G-073 spirit). */
const DECLINE_CONSEQUENCE: Readonly<Record<ApprovalItem['kind'], string>> = {
  task_draft: 'Rejecting closes this draft with your recorded reason; the agent will not brief it.',
  task_start: 'Rejecting closes this approved brief; the agent will not start it.',
  metric_activation: 'Rejecting closes this KPI revision with your recorded reason.',
  metric_value_authoritative:
    'Rejecting closes this reported value version; it will not become authoritative.',
  schedule_activation: 'Rejecting closes this schedule revision; it stays inactive.',
  hubspot_write:
    'Declining is recorded with your reason; this HubSpot draft is closed and nothing is written.',
};

const REQUEST_CHANGES_CONSEQUENCE =
  'Your feedback is recorded against this exact schedule revision. It stays inactive until revised and reviewed again.';

/** Central copy for the shared mandatory-reason control. */
function reviewFormCopy(
  gate: ApprovalItem,
  requesting: boolean,
): {
  readonly consequence: string;
  readonly reasonLabel: string;
  readonly placeholder: string;
  readonly confirmLabel: string;
  readonly confirmText: string;
  readonly cancelLabel: string;
} {
  return requesting
    ? {
        consequence: REQUEST_CHANGES_CONSEQUENCE,
        reasonLabel: `Changes needed: ${gate.title}`,
        placeholder: 'What needs to change? Your feedback is recorded.',
        confirmLabel: `Confirm request changes: ${gate.title}`,
        confirmText: 'Send feedback',
        cancelLabel: 'Cancel the change request',
      }
    : {
        consequence: DECLINE_CONSEQUENCE[gate.kind],
        reasonLabel: `Reason for declining: ${gate.title}`,
        placeholder: 'Why are you declining? The reason is recorded.',
        confirmLabel: `Confirm decline: ${gate.title}`,
        confirmText:
          gate.kind === 'task_start' || gate.kind === 'task_draft'
            ? 'Reject task'
            : 'Confirm rejection',
        cancelLabel: 'Cancel the decline',
      };
}

export interface GateRowProps {
  readonly gate: ApprovalItem;
  /** The human label for this Gate kind (derived display copy, no internal id). */
  readonly kindLabel: string;
  /** True when this row carries the ONE primary button in the list (brief §4). */
  readonly primary: boolean;
  readonly variant: GateRowVariant;
  /** Route the exercise to the owning Gate; the parent surfaces any refusal note. */
  readonly onExercise: () => Promise<RouteResult>;
  /** Route a DECLINE with the mandatory reason (Approvals variant only — BUILD-DEC-116). */
  readonly onDecline?: (reason: string) => Promise<RouteResult>;
  /** Schedule-only response that preserves the draft id and asks the author for a material revision. */
  readonly onRequestChanges?: (reason: string) => Promise<RouteResult>;
}

/** The one exercise control (or the quiet not-bound pill) for a gate row. */
function GateAction({
  gate,
  primary,
  onExercise,
}: Pick<GateRowProps, 'gate' | 'primary' | 'onExercise'>): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const classification = classifyApproval(gate);
  const actionLabel =
    gate.kind === 'task_draft'
      ? 'Approve draft'
      : gate.kind === 'task_start'
        ? 'Approve start'
        : gate.kind === 'metric_activation'
          ? 'Activate KPI'
          : gate.kind === 'schedule_activation'
            ? 'Activate schedule'
            : gate.kind === 'hubspot_write'
              ? 'Approve update'
              : 'Approve value';

  if (classification === 'with_someone_else') {
    return (
      <StatusPill tone="neutral">
        {gate.kind === 'task_draft'
          ? 'With the draft reviewer'
          : gate.kind === 'task_start'
            ? 'With the bound owner'
            : 'Requires decision authority'}
      </StatusPill>
    );
  }
  if (classification === 'setup_needed') {
    return <StatusPill tone="attention">Setup required</StatusPill>;
  }
  return (
    <Btn
      kind={primary ? 'primary' : 'ghost'}
      sm
      disabled={busy}
      ariaLabel={`${actionLabel}: ${gate.title}`}
      onClick={() => {
        if (busy) return;
        setBusy(true);
        void onExercise().finally(() => setBusy(false));
      }}
    >
      {actionLabel}
    </Btn>
  );
}

/** One bounded review-reason form shared by rejection and Schedule change requests. */
function ReviewReasonForm({
  gate,
  mode,
  onConfirm,
  onCancel,
}: {
  readonly gate: ApprovalItem;
  readonly mode: 'decline' | 'request_changes';
  readonly onConfirm: (reason: string) => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [reason, setReason] = useState('');
  const empty = reason.trim().length === 0;
  const requesting = mode === 'request_changes';
  const copy = reviewFormCopy(gate, requesting);
  return (
    <div
      className="decline-form"
      data-decline-form={requesting ? undefined : gate.objectId}
      data-request-changes-form={requesting ? gate.objectId : undefined}
    >
      <p className="df-consequence">{copy.consequence}</p>
      <textarea
        rows={2}
        value={reason}
        aria-label={copy.reasonLabel}
        placeholder={copy.placeholder}
        onChange={(event) => setReason(event.target.value)}
      />
      <div className="df-actions">
        <Btn
          kind="ghost"
          sm
          disabled={empty}
          ariaLabel={copy.confirmLabel}
          onClick={() => {
            if (!empty) onConfirm(reason.trim());
          }}
        >
          {copy.confirmText}
        </Btn>
        <Btn kind="ghost" sm ariaLabel={copy.cancelLabel} onClick={onCancel}>
          Cancel
        </Btn>
      </div>
    </div>
  );
}

/** The decline affordance for a BOUND Approvals row: the control (declinable) or the honest line. */
function DeclineLeg({
  gate,
  onDecline,
}: {
  readonly gate: ApprovalItem;
  readonly onDecline: (reason: string) => Promise<RouteResult>;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!gate.declinable) {
    // Fail closed if a future server projection reports a Gate without a real owning-module decline leg.
    return <p className="decline-none">No decline exists for this gate kind in this build.</p>;
  }
  if (!open) {
    return (
      <Btn kind="ghost" sm ariaLabel={`Decline: ${gate.title}`} onClick={() => setOpen(true)}>
        Decline
      </Btn>
    );
  }
  return (
    <ReviewReasonForm
      gate={gate}
      mode="decline"
      onCancel={() => setOpen(false)}
      onConfirm={(reason) => {
        if (busy) return;
        setBusy(true);
        setOpen(false);
        void onDecline(reason).finally(() => setBusy(false));
      }}
    />
  );
}

/** A Schedule review can request revision without converting the draft into a terminal rejection. */
function RequestChangesLeg({
  gate,
  onRequestChanges,
}: {
  readonly gate: ApprovalItem;
  readonly onRequestChanges: (reason: string) => Promise<RouteResult>;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!open) {
    return (
      <Btn
        kind="ghost"
        sm
        ariaLabel={`Request changes: ${gate.title}`}
        onClick={() => setOpen(true)}
      >
        Request changes
      </Btn>
    );
  }
  return (
    <ReviewReasonForm
      gate={gate}
      mode="request_changes"
      onCancel={() => setOpen(false)}
      onConfirm={(reason) => {
        if (busy) return;
        setBusy(true);
        setOpen(false);
        void onRequestChanges(reason).finally(() => setBusy(false));
      }}
    />
  );
}

/** The gate's title — a live open-the-thread control for a task-start gate, static text otherwise. */
function GateTitle({
  gate,
  className,
}: {
  readonly gate: ApprovalItem;
  readonly className: string;
}): React.JSX.Element {
  const shell = useShell();
  if (gate.kind !== 'task_start' && gate.kind !== 'task_draft') {
    return <div className={className}>{gate.title}</div>;
  }
  return (
    <button
      type="button"
      className={`${className} gate-open`}
      data-open-task={gate.objectId}
      onClick={() => shell.openTaskModal(gate.objectId)}
    >
      {gate.title}
    </button>
  );
}

/** A single pending-gate row in the requested variant. */
export function GateRow(props: GateRowProps): React.JSX.Element {
  return props.variant === 'gate' ? <TodayGateRow {...props} /> : <ApprGateRow {...props} />;
}

/** The Today "Needs you" row shape (prototype .gate). The compact home CTA carries no decline leg —
 *  the decline affordance lives on the Approvals surface (BUILD-DEC-116; one place to say No). */
function TodayGateRow({ gate, primary, onExercise }: GateRowProps): React.JSX.Element {
  const marker = isPendingTone(gate.kind) ? 'marker pending' : 'marker';
  const authLine = authorityLine(gate);
  const readinessNote = gate.readiness?.ready === true ? null : gate.readiness?.note;
  return (
    <div className="gate">
      <span className={marker} aria-hidden="true" />
      <div className="body">
        <GateTitle gate={gate} className="ttl" />
        <div className="sub">
          {gate.why} · <span className="auth">{authLine}</span>
        </div>
        {readinessNote !== null ? (
          <div className="sub">{readinessNote ?? 'Readiness could not be verified.'}</div>
        ) : null}
      </div>
      <GateAction gate={gate} primary={primary} onExercise={onExercise} />
    </div>
  );
}

/** The Approvals inbox row shape (prototype .appr) — the approve action group + the decline leg. */
function ApprGateRow({
  gate,
  kindLabel,
  primary,
  onExercise,
  onDecline,
  onRequestChanges,
}: GateRowProps): React.JSX.Element {
  const marker = isPendingTone(gate.kind) ? 'marker pending' : 'marker';
  const authLine = authorityLine(gate);
  const readyForViewer = isApprovalReadyForViewer(gate);
  const readinessNote = gate.readiness?.ready === true ? null : gate.readiness?.note;
  return (
    <div className="appr">
      <span className={marker} aria-hidden="true" />
      <div className="ab">
        <div className={`ak mono-label k-${KIND_FAMILY[gate.kind]}`}>{kindLabel}</div>
        <GateTitle gate={gate} className="at" />
        <div className="aw">{gate.why}</div>
        {readinessNote !== null ? (
          <div className="aw">{readinessNote ?? 'Readiness could not be verified.'}</div>
        ) : null}
        <div className="ameta">
          <span className="auth">{authLine}</span>
        </div>
      </div>
      <div className="aa">
        <GateAction gate={gate} primary={primary} onExercise={onExercise} />
        {readyForViewer && gate.kind === 'schedule_activation' && onRequestChanges !== undefined ? (
          <RequestChangesLeg gate={gate} onRequestChanges={onRequestChanges} />
        ) : null}
        {readyForViewer && onDecline !== undefined ? (
          <DeclineLeg gate={gate} onDecline={onDecline} />
        ) : null}
      </div>
    </div>
  );
}

/** Rendering copy only; identity's projection and the owning Gate decide the actual authority. */
function authorityLine(gate: ApprovalItem): string {
  if (gate.kind === 'task_draft') {
    return gate.boundToMe === true ? 'you are the bound draft reviewer' : 'with the draft reviewer';
  }
  if (gate.kind === 'task_start') {
    return gate.boundToMe === true ? 'you are the bound owner' : 'with the bound owner';
  }
  return gate.boundToMe === true ? 'you hold decision authority' : 'decision authority required';
}
